import clsx from "clsx";
import LoadingIcon from "components/LoadingIcon";
import EditorLayout from "layouts/EditorLayout";
import React from "react";
import EditorHeader from "./components/EditorHeader";
import GeneralInformation from "./features/GeneralInformation";
import ReferenceUnit from "./features/ReferenceUnit";
import EnvironmentalIndicators from "./features/EnvironmentalIndicators";
import { CustomEPD, epdEditorStore } from "./EPDEditorStore";
import { useLocation, useNavigate, useParams } from "react-router";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import { EPDDetailOUT, FileCategoryType, S3PostData } from "api-client";
import EPDHeader from "./components/EPDHeader";
import Materials from "./features/Materials";
import { observer } from "mobx-react-lite";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { useUploadToS3 } from "hooks/useUploadToS3";
import moment from "moment";
import { ProductActionMode } from "utils";

const EPDEditor = () => {
  const { organisation_id } = useParams();
  const [loading, setLoading] = React.useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { uploadFiles: uploadToS3 } = useUploadToS3();

  React.useEffect(() => {
    setLoading(false);
    return () => epdEditorStore.reset();
  }, []);

  async function loadEPD() {
    if (epdEditorStore.data.id) return;
    setLoading(true);
    const details: void | EPDDetailOUT = await epdSearchStore.fetchEPDDetails(
      location.state.id
    );
    if (!details) return;

    const structeredIndicators = epdEditorStore.restructureIndicators(
      details.indicators
    );

    const userFiles =
      details.file_ids && details.external_links?.user_document
        ? [
            {
              file_id: details.file_ids[0],
              file_name: details.external_links.user_document.file_name,
              url: details.external_links.user_document.url,
            },
          ]
        : [];

    epdEditorStore.setData({
      id: location.state.id,
      name: details.name_de ?? details.name_en,
      manufacturer: details?.manufacturer,
      publishing_date: moment(details.publishing_date).format("YYYY-MM-DD"),
      expiry_year: String(details?.expiry_year),
      data_type: details?.data_type,
      registration_number: details?.registration_number,
      version: details?.version,
      default_material_name: details.concular_material_name_de,
      reference_unit: details.reference_unit,
      reference_factor: details.reference_factor,
      mass_conversion_factor: details.mass_conversion_factor,
      mass_value_m: details.mass_value_m,
      mass_value_m2: details.mass_value_m2,
      mass_value_m3: details.mass_value_m3,
      conformity_name: details.conformity_name,
      indicators: structeredIndicators,
      files: userFiles,
    } as CustomEPD);

    setLoading(false);
  }

  React.useEffect(() => {
    if (location.state) {
      loadEPD();
    }
  }, [location]);

  const uploadFiles = async () => {
    const filesToUpload = epdEditorStore.filesToUpload;
    if (!filesToUpload?.length) return;

    const filesMeta = filesToUpload.map((item) => ({
      file_name: item.name,
      content_type: item?.type,
      category: FileCategoryType.Document,
    }));

    const presignedPosts: void | S3PostData[] = await BPEStore.uploadFilesInfo(
      filesMeta
    );
    if (!presignedPosts) return;
    const result = await uploadToS3(filesToUpload, presignedPosts);
    if (!result) return;
    const uploadedFilesIDs = result.map((item) => item.file_id);
    epdEditorStore.setSelectedFilesIDs(uploadedFilesIDs);
  };

  const saveAndClose = async () => {
    setLoading(true);
    await uploadFiles();
    if (location.state?.mode === ProductActionMode.Edit) {
      await epdEditorStore.editEPD(organisation_id);
    } else {
      await epdEditorStore.createEPD(organisation_id);
    }
    setLoading(false);
    navigate(-1);
  };

  const columnStyle = clsx(
    "px-6 pt-6 text-gray-800 xl:max-h-[calc(100vh-145px)] !max-h-auto lg:overflow-y-auto"
  );

  return (
    <EditorLayout
      header={
        <div>
          <EditorHeader
            saveAndClose={saveAndClose}
            disabled={epdEditorStore.saveValidationErrors}
          />
          <EPDHeader />
        </div>
      }
      className={clsx(
        "xl:!grid-cols-2 !grid-cols-1 !max-h-[calc(100vh-140px)] !min-h-[calc(100vh-140px)]",
        "lg:max-h-full bg-white overflow-y-auto",
        { "!flex w-full items-center justify-center": loading }
      )}
    >
      {loading ? (
        <LoadingIcon type="secondary" className="m-auto !w-20 !h-20" />
      ) : (
        <>
          <div className={clsx(columnStyle, "xl:border-r xl:pb-6")}>
            <GeneralInformation />
            <Materials />
            <ReferenceUnit />
          </div>
          <div className={columnStyle}>
            <EnvironmentalIndicators />
          </div>
        </>
      )}
    </EditorLayout>
  );
};

export default observer(EPDEditor);
